import React, { useState, useEffect, useCallback } from 'react';
import { HiClock } from 'react-icons/hi';
import { useAuth } from '../contexts/AuthContext';
import apiService from '../services/api';
import './TokenExpirationAlert.css';

const TokenExpirationAlert = () => {
  const { logout, refreshUser } = useAuth();
  const [timeRemaining, setTimeRemaining] = useState(300); // 5 minutos em segundos
  const [isVisible, setIsVisible] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const handleAutoLogout = useCallback(async () => {
    setIsVisible(false);
    await logout();
    window.location.href = '/login';
  }, [logout]);

  useEffect(() => {
    const checkTokenExpiration = () => {
      const token = apiService.getToken();
      if (!token) {
        setIsVisible(false);
        return;
      }

      const expirationTime = apiService.getTokenExpiration(token);
      if (!expirationTime) {
        setIsVisible(false);
        return;
      }

      const now = Date.now();
      const timeUntilExpiration = expirationTime - now;
      const fiveMinutesInMs = 5 * 60 * 1000; // 5 minutos em milissegundos

      // Se faltam 5 minutos ou menos para expirar, mostrar o alerta
      if (timeUntilExpiration > 0 && timeUntilExpiration <= fiveMinutesInMs) {
        const secondsRemaining = Math.floor(timeUntilExpiration / 1000);
        setTimeRemaining(secondsRemaining);
        setIsVisible(true);
      } else if (timeUntilExpiration <= 0) {
        // Token já expirou, fazer logout
        handleAutoLogout();
      } else {
        // Ainda tem mais de 5 minutos, esconder o alerta
        setIsVisible(false);
      }
    };

    // Verificar imediatamente
    checkTokenExpiration();

    // Verificar a cada segundo
    const interval = setInterval(() => {
      checkTokenExpiration();
    }, 1000);

    return () => {
      clearInterval(interval);
    };
  }, [handleAutoLogout]);

  const handleRenewToken = async () => {
    try {
      setIsRefreshing(true);
      await apiService.refreshToken();

      // Atualizar dados do usuário no contexto
      if (refreshUser) {
        await refreshUser();
      }

      setIsVisible(false);
      setTimeRemaining(300); // Reset para 5 minutos
    } catch (error) {
      console.error('Erro ao renovar token:', error);
      // Se falhar ao renovar, fazer logout
      await handleAutoLogout();
    } finally {
      setIsRefreshing(false);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  if (!isVisible) {
    return null;
  }

  return (
    <div className="token-expiration-alert-overlay">
      <div className="token-expiration-alert">
        <div className="token-expiration-alert-header">
          <div className="token-expiration-alert-icon">
            <HiClock size={28} />
          </div>
          <h3>Sua Sessão Está Expirando</h3>
        </div>
        <div className="token-expiration-alert-body">
          <p>Sua sessão expirará em:</p>
          <div className="token-expiration-countdown">
            {formatTime(timeRemaining)}
          </div>
          <p className="token-expiration-message">
            Clique em "Permanecer Conectado" para continuar usando o sistema.
          </p>
        </div>
        <div className="token-expiration-alert-actions">
          <button
            className="btn btn-primary"
            onClick={handleRenewToken}
            disabled={isRefreshing}
          >
            {isRefreshing ? 'Renovando...' : 'Permanecer Conectado'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TokenExpirationAlert;
